import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';

import { CONFIG } from '@/config-global';
import { supabase } from '@/lib/supabase';
import { getGuestToken } from '@/services/otp-auth';
import { devError } from '@/utils/dev-console';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ 
  baseURL: CONFIG.serverUrl,
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const providedAuth = (config.headers as any)?.Authorization || (config.headers as any)?.authorization;
    if (!providedAuth) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token) {
        (config.headers as any).Authorization = `Bearer ${session.access_token}`;
      } else {
        const guestToken = getGuestToken();
        if (guestToken) {
          (config.headers as any).Authorization = `Bearer ${guestToken}`;
        }
      }
    }
    if ((config.headers as any).authorization && !(config.headers as any).Authorization) {
      (config.headers as any).Authorization = (config.headers as any).authorization;
      delete (config.headers as any).authorization;
    }

    // Admin impersonation (used by the internal admin portal + create flows).
    try {
      const headers = (config.headers as any) || {};
      const alreadySet =
        typeof headers["X-Admin-Impersonate-User-Id"] === "string" ||
        typeof headers["x-admin-impersonate-user-id"] === "string";

      if (!alreadySet && typeof window !== "undefined") {
        const url = new URL(window.location.href);
        const asUserId = (url.searchParams.get("asUserId") || "").trim();
        if (asUserId) {
          headers["X-Admin-Impersonate-User-Id"] = asUserId;
          config.headers = headers;
        }
      }
    } catch {
      // ignore
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong!')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    devError('Failed to fetch:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  stt: '/api/stt',
  chat: '/api/chat',
  user: '/api/user',
  video: '/api/video',
  admin: '/api/admin',
  public: '/api/public',
  payout: '/api/payout',
  refunds: '/api/refunds',
  booking: '/api/booking',
  product: '/api/product',
  receipts: '/api/receipts',
  referral: '/api/referral',
  purchase: '/api/purchase',
  feedback: '/api/feedback',
  register: '/api/register',
  students: '/api/students',
  messages: '/api/messages',
  community: '/api/community',
  chatToken: '/api/chat/token',
  enrollment: '/api/enrollment',
  productFaq: '/api/product/faq',
  marketplace: '/api/marketplace',
  marketingAnalytics: '/api/marketing-analytics',
  notifyToken: '/api/notify/token',
  transactions: '/api/transactions',
  storageFiles: '/api/storage-files',
  notifications: '/api/notifications',
  productReview: '/api/product/review',
  notifyPublish: '/api/notify/publish',
  stripeConnect: '/api/stripe-connect',
  creatorPublic: '/api/creator/public',
  coursePlanner: '/api/course-planner',
  creatorProfile: '/api/creator/profile',
  previewMedia: '/api/product/preview-media',
  productMarketing: '/api/product-marketing',
};
